<template>
  <div>
    <!-- Both live outside the table: a file input inside a row would be
         re-created every time the list re-renders, which loses the pending
         selection mid-upload. `product_id` says which row the pick belongs to. -->
    <input
      ref="logo_upload"
      type="file"
      accept="image/*"
      style="display: none"
      @change="uploadLogo"
    />
    <input
      ref="sheet_upload"
      type="file"
      accept=".xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      style="display: none"
      @change="uploadSheet"
    />

    <!-- The catalogue divides by product before anything else, so the product
         is the loudest control on the page and the summary strip below it
         describes whichever product is selected.

         "All" leads, because the old screen's Loan Type dropdown started
         cleared -- one list holding every product was the default view, and it
         is the only view in which the Rank column means anything.

         Add product rides on this row rather than in a PageHeader above it. On
         its own row it cost ~50px of the top of the page to show one button --
         and it rendered flush LEFT there, because PageHeader right-aligns via
         space-between across two children and a title-less page renders only
         one. Here it is genuinely right-aligned and the row was already being
         drawn. -->
    <ProductSwitch
      :value="product"
      :counts="visibleProductCounts"
      @change="selectProduct"
    >
      <template #action>
        <v-btn
          v-if="canEdit"
          color="primary"
          depressed
          class="text-none kf-cta"
          @click="openCreate"
        >
          <LucideIcon name="plus" :size="20" class="mr-1" />
          Add product
        </v-btn>
      </template>
    </ProductSwitch>

    <!-- The three figures ARE the state filter: click Enabled to see only
         enabled products, Total products to clear. The counts deliberately
         ignore the state they set, so picking one never zeroes the others.
         They describe the product tab on screen, not the whole catalogue. -->
    <!-- When an admin has fixed this user's state filter the strip narrows to
         the one figure they are allowed and stops being clickable -- StatStrip
         turns itself read-only once no item carries a `filter`. It is not
         hidden: the count is theirs to see. -->
    <StatStrip
      :items="statusItems"
      :active="status"
      :loading="countsLoading"
      @select="selectStatus"
    />

    <FilterBar
      :filters="filterSchema"
      :value="filters"
      :search.sync="search"
      :show-date="false"
      :advanced-count="activeChips.length"
      :result-count="totalCount"
      result-label="products"
      search-placeholder="Search by product name"
      @change="reload"
      @reset="resetFilters"
    >
      <!-- The chips and the applied count both come from the contract module
           (activeFilters over PRODUCT_FILTERS), which is the authority on what
           is applied -- so the bar defers to them rather than inferring its own. -->
      <template #chips>
        <v-chip
          v-for="c in activeChips"
          :key="c.key"
          small
          close
          class="kf-fchip"
          @click:close="clearFilter(c.key)"
        >
          <strong>{{ c.label }}:</strong>&nbsp;{{ c.value }}
        </v-chip>
      </template>
    </FilterBar>

    <!-- Ordering is only explained where it can be used. -->
    <div v-if="canReorder" class="kf-hint">
      <LucideIcon name="grip-vertical" :size="14" />
      Drop a row onto another to place it immediately above that product — only
      those two and the products between them move. Use the row menu to move a
      product to another page.
    </div>

    <!-- Rank is not what QA sees in the app: it counts every product, and the
         app and website skip hidden ones and list one loan type at a time. -->
    <div class="kf-hint">
      <LucideIcon name="eye" :size="14" />
      Rank counts every product, hidden ones too. "Customers see" is the place
      in the app and website: PL 2 = 2nd on the Personal Loan list.
    </div>

    <!-- The recommended budget. Read-only users see it too: it explains the
         badge in the rows, and a count is not a control. -->
    <div class="kf-hint">
      <LucideIcon name="star" :size="14" />
      Recommended:
      <span
        v-for="(u, i) in recommendedUsage"
        :key="u.label"
        class="kf-budget"
        :class="{ 'kf-budget--full': u.full }"
      >
        <template v-if="i">&nbsp;·&nbsp;</template>
        {{ u.label }} {{ u.used }} of {{ MAX_RECOMMENDED_PER_TYPE }}
      </span>
    </div>

    <!-- DataTable owns the loading, empty and error states as well as the
         pager. The rows come through its `body` slot rather than the per-cell
         `item.*` slots: a draggable row needs the <tr> itself, which only the
         body slot hands over. -->
    <DataTable
      class="kf-prod-table"
      title="Products"
      :headers="visibleHeaders"
      :items="items"
      :loading="loading"
      :error="error"
      :page="page"
      :total-pages="totalPages"
      :total-count="totalCount"
      :page-length="pageLength"
      empty-title="No products match these filters"
      empty-message="Try clearing the search or state filter."
      empty-icon="tag-off"
      @page-change="onPage"
      @retry="load"
    >
      <template #actions>
        <DownloadReport :action="downloadReport" />
      </template>
      <template #empty>
        <v-btn
          v-if="canEdit"
          small
          color="primary"
          depressed
          class="text-none"
          @click="openCreate"
        >
          Add the first product
        </v-btn>
      </template>

      <template #body="{ items: rows }">
        <tbody>
          <tr
            v-for="(item, index) in rows"
            :key="item.id"
            class="kf-row"
            :class="{
              'kf-reorder': canReorder,
              'kf-drag-over': drag_over_index === index && drag_index !== null,
              'kf-dragging': drag_index === index,
            }"
            :draggable="canReorder"
            @dragstart="onDragStart(index, $event)"
            @dragover.prevent="onDragOver(index)"
            @dragleave="onDragLeave(index)"
            @drop="onDrop(index)"
            @dragend="onDragEnd"
            @click="openDetail(item)"
          >
            <!-- ----------------------------------------------------- rank ---
                 The product's position in the GLOBAL ranking, which is the unit
                 the move API's to_rank speaks. Under a filter the visible rows
                 are not contiguous, so a number there would be a lie -- hence
                 the dash. -->
            <td>
              <div class="kf-rank">
                <LucideIcon
                  v-if="canReorder"
                  name="grip-vertical"
                  :size="14"
                  class="kf-rank__grip"
                  @click.native.stop
                />
                <span v-if="!isFiltered">{{ globalRank(index) }}</span>
                <span v-else class="kf-dash">&mdash;</span>
              </div>
            </td>

            <!-- ------------------------------------------ customers see ---
                 Worked out server-side over the whole catalogue, so unlike Rank
                 it stays true under any tab, search or filter. -->
            <td>
              <span
                v-if="customerCells[item.id].hidden"
                class="kf-hidden"
                title="Not shown in the app or website"
              >
                Hidden · {{ customerCells[item.id].hidden }}
              </span>
              <div
                v-else-if="customerCells[item.id].place"
                class="kf-cust"
                :title="customerCells[item.id].title"
              >
                <span class="kf-cust__place">#{{ customerCells[item.id].place }}</span>
                <span v-if="customerCells[item.id].types" class="kf-cust__types">
                  {{ customerCells[item.id].types }}
                </span>
              </div>
              <span v-else class="kf-dash">&mdash;</span>
            </td>

            <td><span class="kf-id">{{ item.id }}</span></td>

            <!-- The logo, in the 100x50 slot it had on the old screen. Fixed
                 size rather than intrinsic so every row's logo shares a
                 baseline and a tall image cannot shove its row out of line. -->
            <td>
              <div class="kf-logo" :class="{ 'kf-logo--off': !item.is_active || item.hidden_reason }">
                <img
                  v-if="item.logo"
                  class="kf-logo__img"
                  :src="item.logo"
                  :alt="item.name"
                />
                <span v-else class="kf-logo__empty">No logo</span>
              </div>
            </td>

            <td>
              <div class="kf-prod" :class="{ 'kf-prod--off': !item.is_active }">
                <div class="kf-prod__text">
                  <div class="kf-prod__name" :title="item.name">{{ item.name }}</div>
                  <span v-if="item.flag_recomended" class="kf-rec">
                    <LucideIcon name="star" :size="12" /> Recommended
                  </span>
                </div>
              </div>
            </td>

            <td>{{ item.product_type || "-" }}</td>
            <td class="text-end">{{ item.priority }}</td>

            <!-- ---------------------------------------------- the switches ---
                 Saved on flip, as they were before: these two are what bulk
                 curation of the catalogue is done with, and a confirm dialog on
                 each one turns a pass down the list into a pass with a click
                 between every row.

                 Read-only for anyone without Products Edit rather than hidden.
                 The old screen dropped both columns entirely, which took the
                 information away along with the control -- whether a product is
                 live is worth seeing even if you cannot change it. -->
            <td @click.stop>
              <v-switch
                v-if="canEdit"
                inset
                dense
                hide-details
                class="kf-switch"
                :input-value="item.is_active"
                :disabled="Boolean(updating_ids[item.id])"
                @change="(value) => onActiveChange(item, value)"
              />
              <span
                v-else
                class="kf-flag"
                :class="item.is_active ? 'kf-flag--on' : 'kf-flag--off'"
              >
                <span class="kf-flag__dot"></span>
                {{ item.is_active ? "Enabled" : "Disabled" }}
              </span>
            </td>

            <td @click.stop>
              <!-- Live even when the type is full. A greyed switch says nothing
                   will happen without saying why; letting the click through and
                   answering it is the whole point of the popup below. -->
              <v-switch
                v-if="canEdit"
                inset
                dense
                hide-details
                class="kf-switch"
                :input-value="item.flag_recomended"
                :disabled="Boolean(updating_ids[item.id])"
                @change="(value) => onRecommendedChange(item, value)"
              />
              <span v-else-if="item.flag_recomended" class="kf-rec">
                <LucideIcon name="star" :size="12" /> Yes
              </span>
              <span v-else class="kf-dash">&mdash;</span>
            </td>

            <!-- The cell stops the click, not the menu items: v-menu detaches
                 its content to the app root, so a click inside the menu never
                 reaches this row in the first place. Putting .stop on the items
                 as well is what kept the menu OPEN after an action that does
                 not navigate away, because VMenu's own close handler is what
                 the modifier was cancelling. -->
            <td v-if="canEdit" class="text-end" @click.stop>
              <v-menu offset-y left>
                <template #activator="{ on, attrs }">
                  <v-btn icon small v-bind="attrs" v-on="on" @click.stop>
                    <LucideIcon name="ellipsis-vertical" :size="18" />
                  </v-btn>
                </template>
                <v-list dense>
                  <v-list-item @click="openDetail(item)">
                    <LucideIcon name="eye" :size="16" class="mr-2" />
                    <v-list-item-title class="kf-menu">View details</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="openEdit(item)">
                    <LucideIcon name="pencil" :size="16" class="mr-2" />
                    <v-list-item-title class="kf-menu">Edit product</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="copyUtmLink(item)">
                    <LucideIcon name="copy" :size="16" class="mr-2" />
                    <v-list-item-title class="kf-menu">Copy UTM link</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="triggerLogoUpload(item)">
                    <LucideIcon name="image-up" :size="16" class="mr-2" />
                    <v-list-item-title class="kf-menu">Update logo</v-list-item-title>
                  </v-list-item>
                  <!-- Distinct from Update logo: this re-crops what is already
                       stored, which is how the products saved before the cropper
                       existed get their side bars removed. -->
                  <v-list-item :disabled="!item.logo" @click="recropLogo(item)">
                    <LucideIcon name="crop" :size="16" class="mr-2" />
                    <v-list-item-title class="kf-menu">Crop logo</v-list-item-title>
                  </v-list-item>
                  <v-list-item
                    :disabled="uploading_sheet"
                    @click="triggerSheetUpload(item)"
                  >
                    <LucideIcon name="file-up" :size="16" class="mr-2" />
                    <v-list-item-title class="kf-menu">
                      {{ uploading_sheet ? "Uploading sheet…" : "Upload pincode sheet" }}
                    </v-list-item-title>
                  </v-list-item>

                  <v-divider />

                  <!-- Moves a drag cannot reach: dragging only covers the rows
                       on screen, so crossing a page boundary needs an explicit
                       target. The three that speak in to_rank are hidden under
                       a filter, where a global position means nothing. -->
                  <v-list-item :disabled="is_moving" @click="moveToTop(item)">
                    <LucideIcon name="chevrons-up" :size="16" class="mr-2" />
                    <v-list-item-title class="kf-menu">Move to top</v-list-item-title>
                  </v-list-item>
                  <v-list-item
                    v-if="!isFiltered"
                    :disabled="is_moving || page <= 1"
                    @click="movePageUp(item, index)"
                  >
                    <LucideIcon name="arrow-up" :size="16" class="mr-2" />
                    <v-list-item-title class="kf-menu">Move up a page</v-list-item-title>
                  </v-list-item>
                  <v-list-item
                    v-if="!isFiltered"
                    :disabled="is_moving || page >= totalPages"
                    @click="movePageDown(item, index)"
                  >
                    <LucideIcon name="arrow-down" :size="16" class="mr-2" />
                    <v-list-item-title class="kf-menu">Move down a page</v-list-item-title>
                  </v-list-item>
                  <v-list-item :disabled="is_moving" @click="moveToBottom(item)">
                    <LucideIcon name="chevrons-down" :size="16" class="mr-2" />
                    <v-list-item-title class="kf-menu">Move to bottom</v-list-item-title>
                  </v-list-item>
                  <v-list-item
                    v-if="!isFiltered"
                    :disabled="is_moving"
                    @click="openMoveToPositionDialog(item, index)"
                  >
                    <LucideIcon name="list-ordered" :size="16" class="mr-2" />
                    <v-list-item-title class="kf-menu">Move to position…</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </td>
          </tr>
        </tbody>
      </template>
    </DataTable>

    <!-- ------------------------------------------------- move to position -->
    <v-dialog v-model="move_dialog" max-width="360" persistent>
      <v-card>
        <v-card-title class="text-subtitle-1">
          Move {{ move_target && move_target.name }}
        </v-card-title>
        <v-card-text>
          <v-text-field
            v-model="move_to_rank"
            label="Position"
            type="number"
            min="1"
            :max="totalCount"
            :hint="'1 is shown first, ' + totalCount + ' is last'"
            persistent-hint
            outlined
            dense
            autofocus
            @keyup.enter="confirmMoveToPosition()"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="move_dialog = false">Cancel</v-btn>
          <v-btn color="primary" text @click="confirmMoveToPosition()">Move</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- --------------------------------------------- recommended limit ---- -->
    <RecommendedLimitDialog
      v-model="limit_dialog"
      :product-type="limit_target && limit_target.product_type"
      :product-name="limit_target && limit_target.name"
      :mode="limit_mode"
    />

    <!-- ------------------------------------------------- add / edit dialog -->
    <!-- The form lives in its own component so the product detail page can open
         the same one -- see components/karrofin/ProductFormDialog.vue. -->
    <ProductFormDialog
      v-model="form_dialog"
      :product="editing"
      :default-type="formDefaultType"
      @saved="load"
    />

    <LogoCropDialog
      v-model="crop_dialog"
      :src="crop_src"
      @crop="onCropped"
      @cancel="cancelCrop"
    />
  </div>
</template>

<script>
import StatStrip from "@/components/karrofin/StatStrip.vue";
import DataTable from "@/components/karrofin/DataTable.vue";
import DownloadReport from "@/components/karrofin/DownloadReport.vue";
import FilterBar from "@/components/karrofin/FilterBar.vue";
import ProductSwitch from "@/components/karrofin/ProductSwitch.vue";
import ProductFormDialog from "@/components/karrofin/ProductFormDialog.vue";
import LogoCropDialog from "@/components/karrofin/LogoCropDialog.vue";
import RecommendedLimitDialog from "@/components/karrofin/RecommendedLimitDialog.vue";
import api from "@/app-js/karrofin/api";
import store from "@/app-js/karrofin/store";
import {
  PRODUCT_TYPE,
  PRODUCT_TYPES,
  PRODUCT_FILTER_ALL,
  MAX_RECOMMENDED_PER_TYPE,
} from "@/app-js/karrofin/constants";
import {
  PRODUCT_FILTERS,
  defaultFilters,
  toParams,
  activeFilters,
  clearOne,
} from "@/app-js/karrofin/filters";
import { PAGE_LENGTH } from "@/app-js/karrofin/config";
import { customerPosition } from "@/app-js/karrofin/api/normalise";

/* The permission that separates reading the catalogue from changing it. Held
   in the list the login response puts in sessionStorage; the exact string is
   the TabPermission row's name. */
const EDIT_PERMISSION = "Products Edit";

export default {
  name: "KarroFinOffers",
  components: {
    LogoCropDialog,
    StatStrip,
    DataTable,
    DownloadReport,
    FilterBar,
    ProductSwitch,
    ProductFormDialog,
    RecommendedLimitDialog,
  },
  data() {
    return {
      // Two flags: the rows and the counts arrive from separate requests now
      // (see api.getOffers), so the table paints without waiting on the tiles.
      loading: true,
      countsLoading: true,
      error: null,
      items: [],
      summary: { total: 0, active: 0, inactive: 0 },
      /**
       * Which product's catalogue is on screen, or "All" for every product at
       * once -- the state the old screen's cleared Loan Type dropdown was in,
       * and the only one in which a global Rank is meaningful.
       *
       * Local rather than the shared karrofin store: writing to it here would
       * silently retarget every other screen's product filter the moment
       * someone opened Products. The cost is that this choice does not follow
       * the user to Leads or Applications -- the right trade, since a filter
       * changing behind your back is worse than one that does not travel.
       */
      product: PRODUCT_FILTER_ALL,
      productCounts: [PRODUCT_FILTER_ALL, ...PRODUCT_TYPES].map((label) => ({
        label,
        count: 0,
      })),
      page: 1,
      totalPages: 1,
      totalCount: null,
      pageLength: PAGE_LENGTH,
      status: null,
      search: "",
      filters: defaultFilters(PRODUCT_FILTERS),
      options: { priority_bands: [] },
      recommendedItems: [
        { text: "Yes", value: true },
        { text: "No", value: false },
      ],
      /** Products Edit -- resolved once in created(). */
      canEdit: false,
      /* Recommended slots spent, per product type. Refreshed by load(), which
         every toggle already re-runs, so the budget line and the disabled
         switches can never lag the data by more than one request. */
      recommendedCounts: {},
      MAX_RECOMMENDED_PER_TYPE,
      /**
       * Widths are budgeted for a 1366px laptop minus the 200px legacy drawer.
       * The name column takes the slack; the rest are sized to their content so
       * the numeric columns line up rather than drift apart. Past that the
       * table scrolls inside DataTable's own scroll box, so the page body never
       * scrolls sideways.
       *
       * Since Interest, Tenure and Loan up to moved to the product detail page
       * these add up to about 930px, inside that budget, so the scroll box
       * should no longer have anything to scroll. It stays because the widths
       * are fixed and a narrower window still overflows them.
       */
      headers: [
        { text: "Rank", value: "rank", width: 78, sortable: false },
        { text: "Customers see", value: "customer_rank", width: 128, sortable: false },
        { text: "ID", value: "id", width: 64, sortable: false },
        // Logo leads the product itself, as it did on the old screen. "Fee" is
        // gone with it: the product table has no processing-fee column, so it
        // always rendered empty next to a form field that silently discarded
        // what you typed.
        { text: "Logo", value: "logo", width: 116, sortable: false },
        { text: "Product", value: "name", width: 200 },
        { text: "Type", value: "product_type", width: 122 },
        { text: "Priority", value: "priority", width: 88, align: "end" },
        { text: "Active", value: "is_active", width: 88 },
        { text: "Recommended", value: "flag_recomended", width: 116 },
        { text: "", value: "actions", width: 56, align: "end" },
      ],
      productTypes: PRODUCT_TYPES,
      form_dialog: false,
      editing: null,
      limit_dialog: false,
      limit_target: null,
      limit_mode: "recommend",
      /* Reordering. drag_index doubles as the "a drag is in progress" flag the
         row click reads, so it is cleared in dragend and not in drop. */
      drag_index: null,
      drag_over_index: null,
      is_moving: false,
      move_dialog: false,
      move_target: null,
      move_to_rank: null,
      /* Keyed by product id, so two rows can never be mid-save at once and a
         double click cannot fire the same PATCH twice. */
      updating_ids: {},
      uploading_sheet: false,
      /** Which row a pending file pick belongs to. */
      pending_id: null,
      crop_dialog: false,
      crop_src: null,
      crop_object_url: null,
    };
  },
  computed: {
    /** Filters to draw, in order, each marked for the row or the panel. */
    /**
     * How the two filters draw. What they MEAN -- request parameter, empty
     * value, chip text -- stays in PRODUCT_FILTERS, joined here on `key`.
     *
     * `lock` is the name Team -> Filtered view knows each by, which is not the
     * state key for either of them: they are locked as "priority" and
     * "flag_recomended" but stored as `priority_band` and `recommended`.
     *
     * Neither is pinned, so the compact row is Search plus the buttons.
     */
    filterSchema() {
      const render = [
        {
          key: "priority_band",
          lock: "priority",
          type: "select",
          options: this.options.priority_bands,
          itemText: "name",
          returnObject: true,
        },
        {
          key: "recommended",
          lock: "flag_recomended",
          type: "select",
          options: this.recommendedItems,
        },
      ];
      return render
        .filter((r) => !this.filterLocked("products", r.lock))
        .map((r) => {
          const spec = PRODUCT_FILTERS.find((s) => s.key === r.key);
          if (!spec) return null;
          return {
            key: r.key,
            label: spec.label,
            type: r.type,
            options: r.options || [],
            itemText: r.itemText,
            returnObject: r.returnObject,
            placeholder: "Any",
          };
        })
        .filter(Boolean);
    },
    activeChips() {
      return activeFilters(this.filters, PRODUCT_FILTERS);
    },
    /** The "Customers see" cell per row, keyed by product id. */
    customerCells() {
      const loanType = this.product === PRODUCT_FILTER_ALL ? null : this.product;
      const cells = {};
      this.items.forEach((item) => {
        cells[item.id] = customerPosition(item, loanType);
      });
      return cells;
    },
    /** The actions column is the one thing a read-only user does not get. */
    visibleHeaders() {
      return this.canEdit
        ? this.headers
        : this.headers.filter((h) => h.value !== "actions");
    },
    /* Only the product tabs this user is allowed; every tab when unlocked. A
       pinned user loses the "All" tab with them, which is right -- it would be
       a way to see past their own restriction. */
    visibleProductCounts() {
      return this.allowedSegments(
        "products", "loan_type", this.productCounts, "label");
    },
    /**
     * The recommended budget, per type, as one readable line.
     *
     * Shown on every tab rather than only the one you are on: the cap is
     * per type, so "how full is the other one" is exactly the thing you cannot
     * work out from the rows in front of you.
     */
    recommendedUsage() {
      return PRODUCT_TYPES.map((label) => ({
        label,
        used: this.recommendedCounts[label] || 0,
        full: (this.recommendedCounts[label] || 0) >= MAX_RECOMMENDED_PER_TYPE,
      }));
    },
    /**
     * Is the list narrowed to something less than the whole catalogue?
     *
     * `to_rank` is a position in the GLOBAL ranking, so the Rank column and the
     * page-jump actions only mean anything when nothing is filtering the rows
     * down -- including the product tab, which is itself a filter on loan_type.
     */
    isFiltered() {
      return Boolean(
        this.search ||
        this.status ||
        this.product !== PRODUCT_FILTER_ALL ||
        this.activeChips.length
      );
    },
    /**
     * A move is anchored on the product it is dropped onto, and that anchor is
     * resolved against the global ranking server-side - so a drag stays correct
     * under a search or a filter, where the visible rows are not contiguous.
     * Edit permission is the only gate.
     */
    canReorder() {
      return this.canEdit;
    },
    /** The create form needs a real type; "All" is not one. */
    formDefaultType() {
      return this.product === PRODUCT_FILTER_ALL
        ? PRODUCT_TYPE.PERSONAL_LOAN
        : this.product;
    },
    /**
     * The three state figures, narrowed to the one a pinned user is allowed.
     *
     * The lock stores the raw request value ("true" / "false"); the strip
     * speaks in "active" / "inactive", so the two are mapped here. Dropping
     * `filter` from the surviving entry is what makes StatStrip read-only --
     * the figure stays, the filtering does not.
     */
    statusItems() {
      const all = [
        { label: "Total products", value: this.summary.total, filter: null },
        { label: "Enabled", value: this.summary.active, filter: "active" },
        { label: "Disabled", value: this.summary.inactive, filter: "inactive" },
      ];
      const pinned = this.lockedValues("products", "is_active");
      if (!pinned) return all;
      const wanted = pinned.map((v) =>
        String(v).toLowerCase() === "true" ? "active" : "inactive");
      const kept = all.filter((item) => wanted.indexOf(item.filter) !== -1);
      return kept.length
        ? kept.map((item) => ({ label: item.label, value: item.value }))
        : all;
    },
  },
  created() {
    const permissions =
      JSON.parse(sessionStorage.getItem("permission_list")) || [];
    this.canEdit = permissions.indexOf(EDIT_PERMISSION) !== -1;

    /* Land a pinned member on the product they are allowed. "All" is never one
       of the allowed values, so a pinned user always falls through to here. */
    const pinned = this.lockedValues("products", "loan_type");
    if (pinned && pinned.indexOf(this.product) === -1) {
      this.product = pinned[0];
    }
    this.loadOptions();
    this.load();
  },
  methods: {
    selectProduct(product) {
      this.product = product;
      this.reload();
    },
    /** The report is this list, with these filters -- load()'s params, no paging. */
    downloadReport() {
      return api.exportOffers({
        search_query: this.search,
        product_type: this.product,
        status: this.status,
        ...toParams(this.filters, PRODUCT_FILTERS),
      });
    },
    /** Is this still the newest request? Two are in flight per load now. */
    current(token) {
      return token === this._requestToken;
    },
    load() {
      const token = (this._requestToken = (this._requestToken || 0) + 1);
      this.loading = true;
      this.countsLoading = true;
      this.error = null;
      const { rows, counts } = api.getOffers({
        page_number: this.page,
        page_length: this.pageLength,
        search_query: this.search,
        product_type: this.product,
        status: this.status,
        // toParams is the only writer of filter params -- see filters.js.
        ...toParams(this.filters, PRODUCT_FILTERS),
      });

      rows
        .then((data) => {
          if (!this.current(token)) return;
          this.items = data.results;
          this.totalPages = data.total_page_count;
          this.totalCount = data.total_count;
          // Trust the server's page size over ours -- globalRank() is only
          // right while the two agree.
          if (data.page_length) {
            this.pageLength = data.page_length;
          }
        })
        .catch((err) => {
          if (!this.current(token)) return;
          this.error = err;
          this.items = [];
        })
        .finally(() => {
          if (this.current(token)) this.loading = false;
        });

      counts
        .then((data) => {
          if (!this.current(token)) return;
          // Keep the last good tiles rather than blanking them on failure.
          if (data.summary) this.summary = data.summary;
          this.productCounts = data.product_counts || this.productCounts;
          this.recommendedCounts = data.recommended_counts || {};
        })
        .catch(() => {})
        .finally(() => {
          if (this.current(token)) this.countsLoading = false;
        });
    },
    // The stat strip is the state filter; StatStrip emits null for "all".
    selectStatus(value) {
      this.status = value;
      this.reload();
    },
    clearFilter(key) {
      this.filters = clearOne(this.filters, key, PRODUCT_FILTERS);
      this.reload();
    },
    /** Band lists for the filter panel, cached in the shared store. */
    loadOptions() {
      if (store.filterOptions) {
        this.options = store.filterOptions;
        return;
      }
      api
        .getFilterOptions()
        .then((data) => {
          store.setFilterOptions(data);
          this.options = data;
        })
        .catch(() => {});
    },
    reload() {
      this.page = 1;
      this.load();
    },
    onPage(page) {
      this.page = page;
      this.load();
      // Matches the other table screens: paging from the bottom of a long list
      // should not leave you scrolled past the new rows.
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    // `product` is deliberately untouched: it selects which catalogue you are
    // looking at, which is not the same thing as a filter over it.
    resetFilters() {
      // One assignment covers every filter in the spec, so a filter added later
      // cannot be forgotten here.
      this.filters = defaultFilters(PRODUCT_FILTERS);
      this.status = null;
      this.search = "";
      this.reload();
    },
    /**
     * A row's 1-based position in the GLOBAL ranking, which is the unit the
     * move API's to_rank speaks. Only meaningful while nothing is filtering.
     */
    globalRank(index) {
      return (this.page - 1) * this.pageLength + index + 1;
    },
    openDetail(offer) {
      // Ignore the click that ends a drag gesture.
      if (this.drag_index !== null) return;
      this.$router.push({ name: "karrofin-offer-detail", params: { id: offer.id } });
    },
    openCreate() {
      this.editing = null;
      this.form_dialog = true;
    },
    openEdit(offer) {
      this.editing = offer;
      this.form_dialog = true;
    },

    /* ------------------------------------------------------ inline edits --- */
    /**
     * Flip one boolean on one product.
     *
     * Optimistic: the switch has already moved when this runs, so the row is
     * updated to match and put back if the server refuses. `updating_ids`
     * disables the pair for that row until the answer lands, which is what
     * stops a fast double-flip from racing two PATCHes.
     */
    updateProductField(item, field, value) {
      if (this.updating_ids[item.id]) return;
      const previous = item[field];
      this.$set(item, field, value);
      this.$set(this.updating_ids, item.id, true);

      const call =
        field === "is_active"
          ? api.setOfferActive(item.id, value)
          : api.setOfferRecommended(item.id, value);

      call
        .then(() => {
          this.showSnakeBar("success", "Product updated successfully");
          // Re-read rather than trust the local flip: the state tiles above the
          // table count enabled and disabled products.
          this.load();
        })
        .catch((err) => {
          this.$set(item, field, previous);
          this.showSnakeBar(
            "error",
            this.getApiErrorMessage(err) || "Failed to update product"
          );
        })
        .finally(() => {
          this.$set(this.updating_ids, item.id, false);
        });
    },
    /**
     * Would recommending this product be refused?
     *
     * Both leading conditions carry weight. A product that already holds a slot
     * must answer NO, or flipping one of the five back off would be blocked and
     * the set could never be changed. And a DISABLED product spends nothing, so
     * it can be flagged freely -- what gets refused in that case is enabling it
     * later, which atEnableCap() below picks up.
     */
    atRecommendedCap(item) {
      if (!item.is_active || item.flag_recomended) return false;
      const used = this.recommendedCounts[item.product_type];
      return (used || 0) >= MAX_RECOMMENDED_PER_TYPE;
    },
    /**
     * Would ENABLING this product push its type past the cap?
     *
     * Only enabled products hold a slot, so a disabled one that is already
     * recommended is parked outside the budget -- switching it on is what
     * spends the slot, and is refused exactly like a sixth flag would be.
     */
    atEnableCap(item) {
      if (item.is_active || !item.flag_recomended) return false;
      const used = this.recommendedCounts[item.product_type];
      return (used || 0) >= MAX_RECOMMENDED_PER_TYPE;
    },
    /**
     * Put a switch back where it was.
     *
     * Not a no-op, and not one assignment either. These switches are bound with
     * :input-value, and Vuetify tracks the knob's position internally: once
     * `change` has fired, the knob has already moved, and re-rendering with the
     * SAME prop value it started with leaves it moved. So the value is written
     * forward and then back, one tick apart, to make the prop genuinely change
     * and pull the knob home. Dropping the $nextTick collapses the two writes
     * into one render and the switch silently stays flipped.
     */
    revertSwitch(item, field, attempted) {
      this.$set(item, field, attempted);
      this.$nextTick(() => this.$set(item, field, !attempted));
    },
    /** The Recommended switch. Refuse a sixth here rather than at the server. */
    onRecommendedChange(item, value) {
      if (value && this.atRecommendedCap(item)) {
        this.revertSwitch(item, "flag_recomended", value);
        this.showLimit(item, "recommend");
        return;
      }
      this.updateProductField(item, "flag_recomended", value);
    },
    /** The Active switch, which spends a slot when the product is recommended. */
    onActiveChange(item, value) {
      if (value && this.atEnableCap(item)) {
        this.revertSwitch(item, "is_active", value);
        this.showLimit(item, "enable");
        return;
      }
      this.updateProductField(item, "is_active", value);
    },
    /* The counts are already on the page, so this needs no request -- and the
       serializer still refuses the same write if anything gets past here. */
    showLimit(item, mode) {
      this.limit_target = item;
      this.limit_mode = mode;
      this.limit_dialog = true;
    },
    /**
     * Copy a product's tracking link.
     *
     * navigator.clipboard only exists in a SECURE context, and this panel is
     * served over plain http -- so on every non-HTTPS deployment the object is
     * undefined and the unguarded call the old screen made threw a TypeError,
     * silently doing nothing. Same two-path approach as the detail page.
     */
    copyUtmLink(item) {
      if (!item.utm_link) {
        this.showSnakeBar("error", "UTM Link Not Found");
        return;
      }
      const ok = () => this.showSnakeBar("success", "UTM Link Copied Successfully");
      const fail = () => this.showSnakeBar("error", "Failed to copy UTM Link");

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(item.utm_link).then(ok).catch(fail);
        return;
      }
      // execCommand is deprecated but is the only path on a non-secure origin.
      const el = document.createElement("textarea");
      el.value = item.utm_link;
      el.style.position = "fixed";
      el.style.left = "-9999px";
      document.body.appendChild(el);
      el.select();
      const copied = document.execCommand("copy");
      document.body.removeChild(el);
      if (copied) ok();
      else fail();
    },
    triggerLogoUpload(item) {
      this.pending_id = item.id;
      const input = this.$refs.logo_upload;
      if (!input) return;
      // Cleared first so picking the SAME file twice still fires `change`.
      input.value = "";
      input.click();
    },
    /**
     * Pick -> crop -> upload. This path used to skip BOTH: no type check (the
     * form dialog at least had one) and no crop, so it was the easiest way to
     * land a portrait screenshot in a 2:1 slot.
     */
    uploadLogo() {
      const input = this.$refs.logo_upload;
      const file = input && input.files ? input.files[0] : null;
      input.value = "";
      if (!file || !this.pending_id) return;
      if (!/^image\//.test(file.type)) {
        this.showSnakeBar("error", "Choose an image file");
        this.pending_id = null;
        return;
      }
      this.revokeCropUrl();
      this.crop_object_url = URL.createObjectURL(file);
      this.crop_src = this.crop_object_url;
      this.crop_dialog = true;
    },
    cancelCrop() {
      this.crop_dialog = false;
      this.revokeCropUrl();
      this.crop_src = null;
      this.pending_id = null;
    },
    revokeCropUrl() {
      if (this.crop_object_url) URL.revokeObjectURL(this.crop_object_url);
      this.crop_object_url = null;
    },
    /** Re-crop a logo already saved against this product, with no new file. */
    recropLogo(item) {
      if (!item.logo) return;
      this.pending_id = item.id;
      this.revokeCropUrl();
      this.crop_src = item.logo;
      this.crop_dialog = true;
    },
    onCropped(blob) {
      const id = this.pending_id;
      this.crop_dialog = false;
      if (!id) return;
      const named = new File([blob], "logo.png", { type: "image/png" });

      api
        .uploadOfferLogo(id, named)
        .then(() => {
          this.showSnakeBar("success", "Product logo updated successfully");
          this.load();
        })
        .catch((err) => {
          this.showSnakeBar(
            "error",
            this.getApiErrorMessage(err) || "Failed to upload product logo"
          );
        })
        .finally(() => {
          this.revokeCropUrl();
          this.crop_src = null;
          this.pending_id = null;
        });
    },
    triggerSheetUpload(item) {
      this.pending_id = item.id;
      const input = this.$refs.sheet_upload;
      if (!input) return;
      input.value = "";
      input.click();
    },
    uploadSheet() {
      const input = this.$refs.sheet_upload;
      const file = input && input.files ? input.files[0] : null;
      if (!file || !this.pending_id) return;

      this.uploading_sheet = true;
      api
        .uploadPincodeSheet(this.pending_id, file)
        .then(() => {
          this.showSnakeBar("success", "Sheet Successfully uploaded");
          this.load();
        })
        .catch((err) => {
          this.showSnakeBar(
            "error",
            this.getApiErrorMessage(err) || "Could not upload sheet"
          );
        })
        .finally(() => {
          this.uploading_sheet = false;
          input.value = "";
          this.pending_id = null;
        });
    },

    /* -------------------------------------------------------- reordering --- */
    onDragStart(index, event) {
      if (!this.canReorder) return;
      this.drag_index = index;
      if (event && event.dataTransfer) {
        event.dataTransfer.effectAllowed = "move";
        // Firefox needs data set for a drag to actually start.
        event.dataTransfer.setData("text/plain", String(index));
      }
    },
    onDragOver(index) {
      if (!this.canReorder || this.drag_index === null) return;
      this.drag_over_index = index;
    },
    onDragLeave(index) {
      if (this.drag_over_index === index) {
        this.drag_over_index = null;
      }
    },
    onDrop(index) {
      if (!this.canReorder || this.drag_index === null) return;
      const from = this.drag_index;
      const to = index;
      this.drag_over_index = null;
      if (from === to) {
        this.drag_index = null;
        return;
      }

      const moved = this.items[from];
      const anchor = this.items[to];
      if (!moved || !anchor) {
        this.drag_index = null;
        return;
      }

      // Send an anchor, not a new page order. "Put this product immediately
      // above/below that one" is well defined whatever page we are on and
      // whatever filter is applied, so the server can move exactly one product
      // through the global ranking. Posting the visible page instead only ever
      // let the server shuffle the ranks that page already held, which could
      // not cross a page boundary and dragged every filtered-out product past
      // the one being moved.
      const payload = { product_id: moved.id };
      if (from > to) {
        payload.before_id = anchor.id;
      } else {
        payload.after_id = anchor.id;
      }

      // Optimistic reorder of the visible rows; the reload in moveProduct
      // replaces them with the server's answer either way.
      const list = this.items.slice();
      list.splice(from, 1);
      list.splice(to, 0, moved);
      this.items = list;

      // drag_index stays set until onDragEnd, so the click that ends the
      // gesture does not open the detail page.
      this.moveProduct(payload);
    },
    onDragEnd() {
      this.drag_index = null;
      this.drag_over_index = null;
    },
    /**
     * Move one product to a position defined relative to the others.
     *
     * `payload` carries the product plus exactly one anchor: before_id,
     * after_id, to_rank (1-based global position) or to: 'top' | 'bottom'.
     */
    moveProduct(payload) {
      if (this.is_moving) return;
      this.is_moving = true;
      api
        .moveProduct(payload)
        // Deliberately silent on success, and deliberately unlike the old
        // Products screen, which toasted every reorder. The row is already
        // sitting where it was dropped, so the toast said nothing the screen had
        // not already shown -- and it fired on every drag. Do not "restore
        // parity" and put it back. Failure still speaks, below.
        .catch((err) => {
          this.showSnakeBar(
            "error",
            this.getApiErrorMessage(err) || "Failed to update product order"
          );
        })
        .finally(() => {
          this.is_moving = false;
          // Always re-read: on success to pick up the shifted neighbours, on
          // failure to undo the optimistic splice above.
          this.load();
        });
    },
    moveToTop(item) {
      this.moveProduct({ product_id: item.id, to: "top" });
    },
    moveToBottom(item) {
      this.moveProduct({ product_id: item.id, to: "bottom" });
    },
    movePageUp(item, index) {
      // Ranks past either end are clamped server-side, so this is safe on the
      // first and last page.
      this.moveProduct({
        product_id: item.id,
        to_rank: this.globalRank(index) - this.pageLength,
      });
    },
    movePageDown(item, index) {
      this.moveProduct({
        product_id: item.id,
        to_rank: this.globalRank(index) + this.pageLength,
      });
    },
    openMoveToPositionDialog(item, index) {
      this.move_target = item;
      this.move_to_rank = this.globalRank(index);
      this.move_dialog = true;
    },
    confirmMoveToPosition() {
      const rank = Number(this.move_to_rank);
      this.move_dialog = false;
      if (!this.move_target || !rank || rank < 1) return;
      this.moveProduct({ product_id: this.move_target.id, to_rank: rank });
    },
  },
};
</script>

<style scoped>
/* The one create action on the screen, so it is allowed to be louder than the
   Nunito Sans around it -- Montserrat at 700 is a different voice, not just a
   bigger one. Vuetify ships `.v-btn { font-size: .875rem; font-weight: 500 }`,
   so the class is doubled with `.v-btn` to outrank it. The wide default
   letter-spacing is pulled back in: Montserrat is already wide-set, and
   Vuetify's .089em on top of it reads as spaced out rather than emphatic.
   Left at Vuetify's default 36px height: taller than that would overtake the
   40px filter controls below, which are not the point of the screen. */
.kf-cta.v-btn {
  font-family: "Montserrat", "Nunito Sans", sans-serif;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.01em;
  padding: 0 18px;
}
/* Sits between the filters and the table, so it is the last thing read before
   the rows it describes. */
.kf-hint {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0 2px 8px;
  font-size: var(--kf-fs-label);
  color: var(--kf-text-muted);
}
/* Identity cell. The square is the one brand tint rather than a per-product
   colour: in a list the name already distinguishes the rows, so a palette
   would be decoration that has to be read. */
.kf-prod {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 4px 0;
  min-width: 0;
}
.kf-prod__text {
  min-width: 0;
}
.kf-prod__name {
  font-size: var(--kf-fs-hint);
  font-weight: 600;
  color: #000;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
/* A disabled product is muted, not recoloured -- "switched off" should not
   need a second hue to read. */
.kf-prod--off .kf-prod__name {
  color: #000;
}

/* A spent budget is stated, not alarmed about -- being at five is the normal
   end state of curating a list, not an error. */
.kf-budget {
  font-weight: 600;
}
.kf-budget--full {
  color: var(--kf-brand);
}
/* Rank sits with its grip so the two read as one control. */
.kf-rank {
  display: flex;
  align-items: center;
  gap: 6px;
  font-variant-numeric: tabular-nums;
}
.kf-rank__grip {
  cursor: grab;
  color: var(--kf-text-muted);
}
.kf-rank__grip:active {
  cursor: grabbing;
}
.kf-id {
  font-variant-numeric: tabular-nums;
  color: var(--kf-text-muted);
}
.kf-dash {
  color: var(--kf-text-muted);
}
/* Customers see: the place, then the per-type places in smaller type. */
.kf-cust {
  display: flex;
  flex-direction: column;
  font-variant-numeric: tabular-nums;
  line-height: 1.3;
}
.kf-cust__place {
  font-weight: 600;
}
.kf-cust__types {
  font-size: var(--kf-fs-label);
  color: var(--kf-text-muted);
  white-space: nowrap;
}
.kf-hidden {
  font-size: var(--kf-fs-label);
  color: var(--kf-text-muted);
  white-space: nowrap;
}
/* Vuetify's switch carries 20px of top margin meant for a stacked form field;
   inside a table row that pushes it off the cell's centre line. */
.kf-switch.v-input--switch {
  margin-top: 0;
  padding-top: 0;
}

.kf-flag {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-weight: 700;
}
.kf-flag--on {
  color: var(--kf-brand);
}
.kf-flag--off {
  color: #000;
}
.kf-flag__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
}
.kf-rec {
  font-size: var(--kf-fs-label);
  color: var(--kf-brand);
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 2px;
}
.kf-menu {
  font-size: var(--kf-fs-hint);
}
.kf-dialog__title {
  font-size: 17px;
  font-weight: 700;
  color: var(--kf-brand);
}
.kf-sub {
  font-size: var(--kf-fs-meta);
  font-weight: 700;
  color: #000;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}
</style>

<style>
/* Unscoped, and namespaced to this page's table only. `scoped` cannot reach
   here: the rows above are written in a `body` slot, which Vue compiles in THIS
   component but v-data-table renders as its own children -- and the selectors
   have to cross v-data-table's own markup to get to them. */
.kf-prod-table .kf-reorder {
  cursor: pointer;
}
.kf-prod-table .kf-dragging {
  opacity: 0.5;
}
.kf-prod-table .kf-drag-over > td {
  border-top: 2px solid var(--kf-brand, #1f5fa9) !important;
  background-color: rgba(31, 95, 169, 0.06);
}
</style>
